import { Component, NgZone, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { ClickDiagnosticService } from './services/click-diagnostic/click-diagnostic.service';
import { ThemeService } from './services/theme/theme.service';
import { DisableRightClickService } from './shared/services/disable-right-click.service';

// NOTE: this component used to register router-outlet animations (moveFromTopFade /
// moveFromBottomFade), and app.module imported BrowserAnimationsModule. Both were removed
// (2.13.0 / 2.13.1) deliberately - do not bring them
// back. With BrowserAnimationsModule (even with no animation defined anywhere), the
// leaving page's DOM is removed only after
// its exit animation reports "done"; on a kiosk webview that signal can be lost
// (occluded window, software rendering, a background-timer navigation interrupting
// the transition), which left a dead page - destroyed component, detached click
// handlers - covering the live one. That was the intermittent "page renders but
// ignores every tap" freeze on /payment-promptpay. With the plain DOM renderer, view
// removal is synchronous and the stale-view state cannot exist.
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {

  constructor(
    private rightClickDisable: DisableRightClickService,
    private themeService: ThemeService,
    private clickDiagnosticService: ClickDiagnosticService,
    private router: Router,
    private ngZone: NgZone
  ) {}

  title = 'sc-screenlock-v2-app';

  ngOnInit() {
    this.clickDiagnosticService.start();
    // Re-stamped on every navigation rather than once at bootstrap: the global config is
    // fetched during /home's ngOnInit, so at bootstrap the theme is not yet resolvable.
    this.themeService.applyThemeToDocument();
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.themeService.applyThemeToDocument();
        this.drawNavigationStartedOutsideZone(event as NavigationEnd);
      });
  }

  /**
   * Safety net for a navigation started outside the Angular zone. The router still changes
   * the URL and creates the page's component, but no change detection follows, so none of
   * the page's `*ngIf` content, ngOnInit or ngAfterViewInit ever happens: a white page with
   * no error anywhere, and nothing left in the zone to wake it. That is what the booths saw
   * on /payment-success when the Pocketbase realtime "paid" event arrived after the SDK had
   * reconnected its EventSource (zone.js does not patch `EventSource.onerror`). Each known
   * caller now runs inside the zone itself; this catches the next one.
   */
  private drawNavigationStartedOutsideZone(event: NavigationEnd) {
    if (NgZone.isInAngularZone()) {
      return;
    }
    console.warn(`navigation to ${event.urlAfterRedirects} ended outside the Angular zone; forcing change detection`);
    // Entering and leaving the zone with nothing pending emits onMicrotaskEmpty, which is
    // what ApplicationRef.tick() listens for.
    this.ngZone.run(() => { });
  }
}
